import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SubpageLayout from "@/components/SubpageLayout";
import { getLegal } from "@/lib/api";

const YEAR = new Date().getFullYear();

const fmtDate = (iso) => {
    try {
        return new Date(iso).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "long",
            year: "numeric",
        });
    } catch {
        return "";
    }
};

/**
 * LegalPage — shared editorial template for Privacy / Terms.
 * Fetches content from /api/legal/{page} so admin edits reflect live.
 */
export default function LegalPage({
    slug,
    eyebrow,
    titleLeft,
    titleAccent,
    counterpart,
}) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        getLegal(slug)
            .then(setData)
            .catch(() => setData({ sections: [], updated_at: "" }))
            .finally(() => setLoading(false));
    }, [slug]);

    const sections = data?.sections || [];

    return (
        <SubpageLayout>
            <article
                data-testid={`legal-${slug}`}
                className="mx-auto max-w-[1100px] px-6 md:px-12 lg:px-20 py-20 md:py-32"
            >
                <p className="eyebrow text-[#1B1B1B]/60 mb-6">§ {eyebrow}</p>
                <h1 className="font-serif-editorial text-5xl md:text-7xl lg:text-[92px] leading-[0.98] tracking-[-0.02em] mb-4">
                    {titleLeft}{" "}
                    <em className="italic text-[#A8844F]">{titleAccent}</em>
                </h1>
                <p
                    data-testid={`legal-${slug}-updated`}
                    className="eyebrow text-[#1B1B1B]/60 mb-16"
                >
                    Last updated · {fmtDate(data?.updated_at)}
                </p>

                {loading ? (
                    <p className="eyebrow text-[#1B1B1B]/50">Loading…</p>
                ) : (
                    <div className="border-t border-[#E6E2D6]">
                        {sections.map((s, i) => (
                            <section
                                key={i}
                                className="grid grid-cols-12 gap-6 md:gap-10 py-10 md:py-16 border-b border-[#E6E2D6]"
                            >
                                <div className="col-span-2 md:col-span-1">
                                    <span className="font-serif-editorial italic text-[#A8844F] text-3xl md:text-5xl leading-none">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                </div>
                                <div className="col-span-10 md:col-span-4">
                                    <h2 className="font-serif-editorial text-2xl md:text-3xl leading-[1.1]">
                                        {s.title}
                                    </h2>
                                </div>
                                <div className="col-span-12 md:col-span-7 space-y-4">
                                    {(s.body || []).map((p, j) => (
                                        <p
                                            key={j}
                                            className="font-sans-tight text-base md:text-lg leading-[1.7] text-[#1B1B1B]/85 font-light max-w-xl"
                                        >
                                            {p}
                                        </p>
                                    ))}
                                </div>
                            </section>
                        ))}
                    </div>
                )}

                <div className="mt-16 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <p className="eyebrow text-[#1B1B1B]/60">
                        © {YEAR} Depmont · Independent Creative Company
                    </p>
                    {counterpart && (
                        <Link
                            to={counterpart.to}
                            className="eyebrow link-underline text-[#1B1B1B]"
                        >
                            {counterpart.label} →
                        </Link>
                    )}
                </div>
            </article>
        </SubpageLayout>
    );
}
